import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// Configure axios defaults
const baseURL = process.env.NODE_ENV === 'production' 
  ? window.location.origin  // Use current domain in production
  : 'http://localhost:5050'; // Use localhost in development

axios.defaults.baseURL = baseURL;
// Note: withCredentials should be false when backend has supports_credentials=False
axios.defaults.withCredentials = false;

// Define available permissions
export type Permission = 
  | 'view_prerequisites'
  | 'manage_prerequisites'
  | 'view_attendance'
  | 'manage_attendance'
  | 'view_courses'
  | 'manage_courses'
  | 'view_course_builder'
  | 'manage_course_builder'
  | 'view_temp_cards'
  | 'manage_temp_cards'
  | 'view_test_sheet_submissions'
  | 'manage_test_sheet_submissions'
  | 'view_dob_verifier'
  | 'manage_dob_verifier'
  | 'manage_users'
  | 'admin_access';

// Define permission groups for easy role management
export const PERMISSION_GROUPS = {
  PREREQUISITES: ['view_prerequisites', 'manage_prerequisites'] as Permission[],
  ATTENDANCE: ['view_attendance', 'manage_attendance'] as Permission[],
  COURSES: ['view_courses', 'manage_courses'] as Permission[],
  COURSE_BUILDER: ['view_course_builder', 'manage_course_builder'] as Permission[],
  TEMP_CARDS: ['view_temp_cards', 'manage_temp_cards'] as Permission[],
  TEST_SHEET_SUBMISSIONS: ['view_test_sheet_submissions', 'manage_test_sheet_submissions'] as Permission[],
  DOB_VERIFIER: ['view_dob_verifier', 'manage_dob_verifier'] as Permission[],
  USER_MANAGEMENT: ['manage_users'] as Permission[],
  ADMIN: ['admin_access'] as Permission[],
};

// Default role permissions
export const ROLE_PERMISSIONS: Record<string, Permission[]> = {
  admin: [
    ...PERMISSION_GROUPS.PREREQUISITES,
    ...PERMISSION_GROUPS.ATTENDANCE, 
    ...PERMISSION_GROUPS.COURSES,
    ...PERMISSION_GROUPS.COURSE_BUILDER,
    ...PERMISSION_GROUPS.TEMP_CARDS,
    ...PERMISSION_GROUPS.TEST_SHEET_SUBMISSIONS,
    ...PERMISSION_GROUPS.DOB_VERIFIER,
    ...PERMISSION_GROUPS.USER_MANAGEMENT,
    ...PERMISSION_GROUPS.ADMIN,
  ],
  user: [
    ...PERMISSION_GROUPS.TEMP_CARDS,
  ],
};

interface User {
  id: string; // Salesforce IDs are strings
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  permissions?: Permission[]; // Custom permissions override
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<string>;
  hasPermission: (permission: Permission) => boolean;
  hasAnyPermission: (permissions: Permission[]) => boolean;
  hasAllPermissions: (permissions: Permission[]) => boolean;
  getUserPermissions: () => Permission[];
  canAccessModule: (module: 'prerequisites' | 'attendance' | 'courses' | 'course_builder' | 'temp_cards' | 'test_sheet_submissions' | 'dob_verifier' | 'users') => boolean;
}

// Helper function to get user permissions
const getUserPermissions = (user: User | null): Permission[] => {
  if (!user) return [];
  
  // If user has custom permissions, use those
  if (user.permissions && user.permissions.length > 0) {
    return user.permissions;
  }
  
  // Otherwise, use role-based permissions
  return ROLE_PERMISSIONS[user.role] || [];
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Permission checking functions
  const hasPermission = (permission: Permission): boolean => {
    const userPermissions = getUserPermissions(user);
    return userPermissions.includes(permission) || userPermissions.includes('admin_access');
  };

  const hasAnyPermission = (permissions: Permission[]): boolean => {
    return permissions.some(permission => hasPermission(permission));
  };

  const hasAllPermissions = (permissions: Permission[]): boolean => {
    return permissions.every(permission => hasPermission(permission));
  };

  const getPermissions = (): Permission[] => {
    return getUserPermissions(user);
  };

  const canAccessModule = (module: 'prerequisites' | 'attendance' | 'courses' | 'course_builder' | 'temp_cards' | 'test_sheet_submissions' | 'dob_verifier' | 'users'): boolean => {
    switch (module) {
      case 'prerequisites':
        return hasAnyPermission(['view_prerequisites', 'manage_prerequisites']);
      case 'attendance':
        return hasAnyPermission(['view_attendance', 'manage_attendance']);
      case 'courses':
        return hasAnyPermission(['view_courses', 'manage_courses']);
      case 'course_builder':
        return hasAnyPermission(['view_course_builder', 'manage_course_builder']);
      case 'temp_cards':
        return hasAnyPermission(['view_temp_cards', 'manage_temp_cards']);
      case 'test_sheet_submissions':
        return hasAnyPermission(['view_test_sheet_submissions', 'manage_test_sheet_submissions']);
      case 'dob_verifier':
        return hasAnyPermission(['view_dob_verifier', 'manage_dob_verifier']);
      case 'users':
        return hasPermission('manage_users');
      default:
        return false;
    }
  };

  // On initial load, try to load user from localStorage
  useEffect(() => {
    try {
      const storedUser = localStorage.getItem('user');
      const storedToken = localStorage.getItem('accessToken');

      if (storedUser && storedToken) {
        setUser(JSON.parse(storedUser));
        axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      }
    } catch (e) {
      console.error("Failed to parse user from localStorage", e);
      setUser(null);
      localStorage.clear();
    } finally {
      setLoading(false);
    }
  }, []);

  const login = async (email: string, password: string) => {
    try {
      setError(null);
      setLoading(true);
      const response = await axios.post('/auth/login', { email, password });
      
      if (response.data && response.data.user && response.data.access_token) {
        const { user: userData, access_token, refresh_token } = response.data;
        
        // Set user and tokens
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('accessToken', access_token);
        localStorage.setItem('refreshToken', refresh_token);
        
        // Set default auth header for subsequent requests
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      } else {
        throw new Error('Invalid login response from server');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Login failed');
      // Clear any partial login data
      localStorage.clear();
      setUser(null);
      delete axios.defaults.headers.common['Authorization'];
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = useCallback(async () => {
    // No need to call a backend endpoint if we are just clearing local tokens
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  const refreshToken = useCallback(async (): Promise<string> => {
    const storedRefreshToken = localStorage.getItem('refreshToken');
    if (!storedRefreshToken) {
      throw new Error('No refresh token available.');
    }
    try {
      const response = await axios.post('/auth/refresh', {}, {
        headers: { 'Authorization': `Bearer ${storedRefreshToken}` }
      });

      if (response.data && response.data.access_token) {
        const { access_token } = response.data;
        localStorage.setItem('accessToken', access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        return access_token;
      } else {
        throw new Error('Invalid refresh response from server');
      }
    } catch (err) {
      console.error('Token refresh failed:', err);
      // If refresh fails, log the user out completely
      await logout();
      throw err;
    }
  }, [logout]);
  
  // Setup axios interceptor to automatically refresh token
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        const requestUrl = originalRequest?.url || '';
        const shouldRefresh =
          error.response?.status === 401 &&
          originalRequest &&
          !originalRequest._retry &&
          !requestUrl.includes('/auth/login') &&
          !requestUrl.includes('/auth/refresh');

        if (shouldRefresh) {
          originalRequest._retry = true;
          try {
            const newAccessToken = await refreshToken();
            originalRequest.headers = {
              ...originalRequest.headers,
              Authorization: `Bearer ${newAccessToken}`,
            };
            return axios(originalRequest);
          } catch (refreshError) {
            return Promise.reject(refreshError);
          }
        }
        return Promise.reject(error);
      }
    );
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [refreshToken]);


      const value = {
      user,
      loading,
      error,
      login,
      logout,
      refreshToken,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      getUserPermissions: getPermissions,
      canAccessModule,
    };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 
